import { useEffect } from 'react';
import * as THREE from 'three';
import { axialToWorld } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import type { Cell } from '@/game/sim';
import { useSimFrame } from './app/simFrame';
import { flatRing, InstancedMarkers, type MarkerPoint } from './InstancedMarkers';
import { PENDING_MOVE_MS, type PendingMove, pendingMoveDone } from './pendingMove';
import { type UiStore, useUi } from './ui/store';

interface MoveTargetMarkerProps {
  /** Their destinations are ringed; own settlers sent to found a city (`foundAt`) too, selected or not. */
  selectedUnitIds: readonly string[];
  /** Holds the move just clicked, ringed until the snapshot shows it (or it times out). */
  ui: UiStore;
}

const TILE_THICKNESS = config.hexMap.tile.thickness;
const MARKER_Y = TILE_THICKNESS + 0.06;

const markerGeometry = flatRing(0.34, 0.05, 24);
const markerMaterial = new THREE.MeshStandardMaterial({
  color: '#34d399',
  emissive: '#34d399',
  emissiveIntensity: 0.9,
});
// A pending founding spot: a wider ring in the accent colour (--color-accent)
// with a small pennant, so it reads apart from a move.
const foundGeometry = flatRing(0.42, 0.045, 32);
const poleGeometry = new THREE.CylinderGeometry(0.018, 0.018, 0.42, 6);
const pennantGeometry = new THREE.ConeGeometry(0.09, 0.2, 3);
pennantGeometry.rotateZ(-Math.PI / 2);
const foundMaterial = new THREE.MeshStandardMaterial({
  color: '#7dd3fc',
  emissive: '#7dd3fc',
  emissiveIntensity: 0.9,
});
const POLE_OFFSET: MarkerPoint = { x: 0, y: 0.21, z: 0 };
const PENNANT_OFFSET: MarkerPoint = { x: 0.1, y: 0.36, z: 0 };

/** One point per distinct cell, at height `y`. */
function cellPoints(cells: Iterable<Cell>, y: number): MarkerPoint[] {
  const seen = new Set<string>();
  const points: MarkerPoint[] = [];
  for (const cell of cells) {
    const key = `${cell.q},${cell.r}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const world = axialToWorld(cell, config.hexMap.tile.radius);
    points.push({ x: world.x, y, z: world.z });
  }
  return points;
}

/** Drops the pending move once confirmed, or when its time is up. */
function usePendingMoveExpiry(ui: UiStore, pending: PendingMove | null, confirmed: boolean) {
  useEffect(() => {
    if (!pending) return;
    if (confirmed) {
      ui.set({ pendingMove: null });
      return;
    }
    const left = Math.max(0, pending.at + PENDING_MOVE_MS - Date.now());
    const id = setTimeout(() => ui.set({ pendingMove: null }), left);
    return () => clearTimeout(id);
  }, [ui, pending, confirmed]);
}

export function MoveTargetMarker({ selectedUnitIds, ui }: MoveTargetMarkerProps) {
  const { view: state, playerId } = useSimFrame();
  const pending = useUi(ui, (s) => s.pendingMove);
  const confirmed = pending !== null && pendingMoveDone(pending, state.units, Date.now());
  usePendingMoveExpiry(ui, pending, confirmed);
  // Every own settler's pending founding spot.
  const founding: Cell[] = [];
  for (const unit of Object.values(state.units)) {
    if (unit.owner !== playerId || unit.order?.kind !== 'found') continue;
    founding.push(unit.order.target);
  }
  // One ring per distinct destination among the selected units.
  const dests: Cell[] = [];
  for (const id of selectedUnitIds) {
    const unit = state.units[id];
    if (!unit?.path || unit.path.length === 0 || unit.order?.kind === 'found') continue;
    // A move onto water etc. walks to the nearest reachable tile, but the
    // marker stays on the tile the player picked.
    dests.push(
      unit.order?.kind === 'move' || unit.order?.kind === 'attackMove'
        ? unit.order.target
        : unit.path[unit.path.length - 1]
    );
  }
  if (pending && !confirmed) dests.push(pending.cell);
  const found = cellPoints(founding, MARKER_Y);

  return (
    <>
      <InstancedMarkers
        geometry={markerGeometry}
        material={markerMaterial}
        points={cellPoints(dests, MARKER_Y)}
      />
      <InstancedMarkers geometry={foundGeometry} material={foundMaterial} points={found} />
      <InstancedMarkers
        geometry={poleGeometry}
        material={foundMaterial}
        points={found}
        offset={POLE_OFFSET}
      />
      <InstancedMarkers
        geometry={pennantGeometry}
        material={foundMaterial}
        points={found}
        offset={PENNANT_OFFSET}
      />
    </>
  );
}

const attackGeometry = flatRing(0.46, 0.06, 32);
const attackMaterial = new THREE.MeshStandardMaterial({
  color: '#f87171',
  emissive: '#f87171',
  emissiveIntensity: 0.9,
});

/** Red rings on the targets troops were sent at (Send troops), until each attack resolves. */
export function AttackTargetMarkers({ cells }: { cells: readonly Cell[] }) {
  return (
    <InstancedMarkers
      geometry={attackGeometry}
      material={attackMaterial}
      points={cellPoints(cells, MARKER_Y + 0.02)}
    />
  );
}
